/**
 * MonadicOpenEndCard — the UNIFIED open-end view for a monadic stage.
 *
 * A monadic open-end runs one isolated pass per variant. Instead of three disconnected
 * per-variant theme cards (whose labels don't line up, so nothing compares), this renders
 * a single comparable view built on the backend's SHARED codeframe:
 *   - Themes: ONE grouped bar — theme buckets as rows, one sub-bar per variant present in
 *     the bucket. Sub-bars share ONE uniform variant color (distinct A/B colors are reserved
 *     for a dedicated same-plot comparison chart); each sub-bar is identified by its inline
 *     text label, not a color legend.
 *   - Quotes: ONE merged gallery — featured quote on top, then a grid of attributed cards
 *     (portrait, name, blurb, verbatim), each tagged with a variant chip.
 *
 * Net-new, presentational, render-only. Reuses the existing open-end primitives
 * (buildQuoteItems / QuoteCard) so attribution + rendering stay
 * consistent with the non-monadic card. No fetching, no global state.
 */
import { useMemo, useState } from 'react'

import { getAppChartPalette } from '@/lib/charts/chartjs'
import { cn } from '@/lib/utils'
import { ComparisonLabelText } from './ComparisonLabelText'
import { deriveThemeColumns, MonadicThemeHeatmap } from './MonadicThemeHeatmap'
import { groupThemesBySentiment, themeDisplayName } from './openEndThemeGroups'
import { useThemeOtherDescriptions, useThemeOtherLabel } from './useThemeOtherLabel'
import { PlotCarousel } from './PlotCarousel'
import { MonadicThemesPlot, OpenEndQuotesPlot, PerVersionThemesPlot, readMonadicThemePlot, readPerVersionThemePlots, readQuotePlot, readThemeHeatPlot, ThemeHeatGridPlot, type MonadicThemePlot, type ThemeHeatPlot } from './OpenEndPlots'

import {
  buildQuoteItems,
  FeaturedQuote,
  isOtherTheme,
  pickFeaturedIndex,
  QuoteCard,
  SentimentChip,
  SentimentGroupChip,
  themeBarFractions,
  THEME_BAR_TRACK_CLASS,
  THEME_BAR_HEIGHT_CLASS,
  type OpenEndResponse,
  type OpenEndTheme,
  type QuoteItem,
} from './QuickStudyOpenEndCard'
import type { QuickStudyPersona } from './QuickStudyTwinsPanel'

// The two views a monadic open-end card can show. Exported so an external (controlled)
// header toggle — e.g. the PlotCard header in MessageRow — shares one source of truth.
export type MonadicTab = 'themes' | 'quotes'

// One variant's share of a shared codeframe bucket (mirrors the backend MonadicThemeShare).
export type MonadicThemeShare = {
  creative_id: string
  label: string
  count: number
  percent: number
}

// One shared codeframe bucket coded across every variant (mirrors backend MonadicTheme).
export type MonadicThemeT = {
  theme: string
  is_other?: boolean
  description?: string
  sentiment?: string
  variants: MonadicThemeShare[]
}

// One variant's isolated read (the subset of per_item this card reads).
export type MonadicPerItem = {
  creative_id: string
  label: string
  n: number
  // Per-variant themes computed individually (used as fallback when monadicThemes is empty).
  themes?: Array<OpenEndTheme>
  responses: OpenEndResponse[]
  // Wave 7 f1: this version's own themes as a stamped plot (no shared codeframe only).
  theme_plot?: unknown
}

export type MonadicOpenEndCardProps = {
  monadicThemes: MonadicThemeT[]
  perItem: MonadicPerItem[]
  question?: Record<string, unknown>
  personas?: QuickStudyPersona[]
  /** Comparison entity labels (e.g. "Landing page A") bolded in theme + quote text. */
  comparisonLabels?: string[]
  /** The construct under test (Result.title, e.g. "First impression") — rendered as a small
   *  label directly above the exhibit (mirrors the QsCardChrome `construct` header slot this
   *  card used pre-merge). Optional so existing standalone usages render title-free. */
  construct?: string
  /** Report (compact) only — page the grouped theme topics through a carousel (one topic per
   *  slide, all variants per slide) instead of a tall stacked column. Off (stacked) in chat. */
  themesAsCarousel?: boolean
  /** Report only — hide the Quotes tab (the full attributed verbatims live in the chat insight,
   *  not the findings summary). Off in chat, where Themes + Quotes both show. */
  hideQuotes?: boolean
  /** Report document — show themes + quotes inline with no tabs, carousels, or load-more gates. */
  documentMode?: boolean
  /** Controlled-tab mode (chat / PlotCard). When provided, the card renders ONLY the matching
   *  view and suppresses BOTH the internal Themes|Quotes toggle and the internal construct header
   *  (`qs-card-chrome-construct`) — construct/title is owned by PlotCard upstream. When absent, the
   *  card is uncontrolled (byte-identical to before): internal toggle + construct header + local
   *  tab state. Report (`themesAsCarousel`/`hideQuotes`) and `documentMode` paths are unaffected. */
  activeTab?: MonadicTab
  /** Notifies the parent when the internal toggle is used. Only meaningful in uncontrolled mode
   *  (in controlled mode the toggle lives upstream); accepted for symmetry with the header slot. */
  onTabChange?: (t: MonadicTab) => void
  /** Chart-type choice for the themes view; the outer PlotCard chart-type switch drives it,
   *  default heatmap for 2+ assets. `'heatmap'` renders the sentiment-grouped heatmap when 2+
   *  columns exist; `'bars'` forces the grouped stacked-bars fallback. */
  themesView?: 'heatmap' | 'bars'
  /** Wave 7 (Q13): Python's stamped `monadic_theme_plot` (grouped-bars). Drawn wherever the card
   *  draws bars over the shared codeframe; stored results keep GroupedThemeBar's legacy bars. */
  monadicThemePlot?: unknown
  /** W7-T4b (Q14): Python's stamped `monadic_theme_heat_plot` (heat-grid, themes x versions). Drawn
   *  by the heatmap view; stored results keep `MonadicThemeHeatmap` as its legacy reader. */
  monadicThemeHeatPlot?: unknown
  /** Wave 7 (Q16): Python's stamped `quote_plot` (quote-list gallery, a section per version).
   *  Stored results keep the browser-built gallery as their legacy reader. */
  quotePlot?: unknown
  className?: string
}

// Variant→color: a SINGLE uniform color for every creative. Creatives are identified by their
// text label (next to each bar / on each quote chip), so we never color-code A vs B in these
// readouts — distinct per-creative colors belong only inside a dedicated same-plot comparison
// chart. Kept as a map (keyed by creative_id) so callers stay unchanged. Pure + deterministic.
export function buildVariantColors(
  perItem: MonadicPerItem[],
  palette: string[],
): Record<string, string> {
  const uniform = palette[0]
  const out: Record<string, string> = {}
  for (const item of perItem) {
    out[item.creative_id] = uniform
  }
  return out
}

function MonadicQuotesCount({ count }: { count: number }) {
  return (
    <span
      className="shrink-0 text-xs tabular-nums text-twin-chat-fg-muted"
      data-testid="qs-monadic-quotes-count"
    >
      {count} quote{count === 1 ? '' : 's'}
    </span>
  )
}

// ── per-variant fallback bars (when no shared codeframe is available) ───────────
// Renders each variant's individually-coded themes as its own horizontal-bar section.
// This avoids an empty state: even if the shared reconciliation didn't run, each variant
// still has its own clustered themes that are worth showing.
const MAX_VARIANT_THEMES = 5

function PerVariantThemeFallback({
  perItem,
  variantColor,
  comparisonLabels = [],
  documentMode = false,
}: {
  perItem: MonadicPerItem[]
  variantColor: Record<string, string>
  comparisonLabels?: string[]
  documentMode?: boolean
}) {
  const otherLabel = useThemeOtherLabel()
  const otherDescriptions = useThemeOtherDescriptions()
  const itemsWithThemes = perItem.filter((item) => (item.themes?.length ?? 0) > 0)

  if (itemsWithThemes.length === 0) {
    if (documentMode) {
      return null
    }
    return (
      <p className="px-1 py-6 text-center text-sm text-twin-chat-fg-muted" data-testid="qs-monadic-no-themes">
        No themes were clustered for these variants — check the Quotes tab for the verbatims.
      </p>
    )
  }

  return (
    <div className="space-y-5 px-1 pt-1" data-testid="qs-monadic-per-variant-bars">
      {/* This is the fallback: themes were coded separately per variant (no shared codeframe), so
          the rows across sections are NOT the same theme and must not be read as a head-to-head
          comparison. Caption it so a marketer doesn't line up A's row against B's. */}
      {itemsWithThemes.length > 1 && (
        <p className="text-xs italic text-twin-chat-fg-muted" data-testid="qs-monadic-fallback-note">
          Themes coded separately per page; not directly comparable.
        </p>
      )}
      {itemsWithThemes.map((item) => {
        const top = (item.themes ?? []).slice(0, MAX_VARIANT_THEMES)
        const fractions = themeBarFractions(top)
        // Keep each theme's bar width tied to its ORIGINAL top-list position after grouping
        // reorders the themes into sentiment sections.
        const fractionByTheme = new Map<OpenEndTheme, number>()
        top.forEach((t, i) => fractionByTheme.set(t, fractions[i]))
        const color = variantColor[item.creative_id]
        const { groups, other } = groupThemesBySentiment(top)

        // The section header now carries the sentiment, so the per-row chip is dropped here.
        const renderThemeBar = (t: OpenEndTheme) => {
          const pct = typeof t.percent === 'number' ? `${Math.round(t.percent)}%` : null
          const n = typeof t.count === 'number' ? `${t.count}` : null
          const share = pct && n ? `${pct} · ${n}` : pct ?? (n ? `${n} mention${t.count === 1 ? '' : 's'}` : '')
          const widthPct = Math.max(3, Math.round((fractionByTheme.get(t) ?? 0) * 100))
          return (
            <li key={t.theme} data-testid="qs-monadic-theme-bar-row">
              <div className="flex items-baseline justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="min-w-0 truncate text-sm font-medium text-twin-chat-fg"
                    title={isOtherTheme(t) ? otherDescriptions.floor : t.description || t.theme}
                  >
                    <ComparisonLabelText text={themeDisplayName(t, otherLabel)} labels={comparisonLabels} />
                  </span>
                </span>
                {share && (
                  <span className="shrink-0 text-xs tabular-nums text-twin-chat-fg-muted">{share}</span>
                )}
              </div>
              <div className={cn('mt-1 w-full overflow-hidden rounded-full', THEME_BAR_HEIGHT_CLASS, THEME_BAR_TRACK_CLASS)}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${widthPct}%`, background: color }}
                />
              </div>
            </li>
          )
        }

        return (
          <div key={item.creative_id} data-testid="qs-monadic-per-variant-section">
            <div className="mb-2 flex items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
              <span className="text-xs font-medium text-twin-chat-fg-muted">
                <ComparisonLabelText text={item.label} labels={comparisonLabels} />
              </span>
            </div>
            <div className="space-y-3">
              {groups.map((g) => (
                <div key={g.group} className="space-y-2">
                  <SentimentGroupChip group={g.group} />
                  <ul className="space-y-2.5">{g.themes.map((t) => renderThemeBar(t))}</ul>
                </div>
              ))}
              {other.length > 0 && (
                <ul className="space-y-2.5">{other.map((t) => renderThemeBar(t))}</ul>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── themes (grouped bar) ───────────────────────────────────────────────────────
// One topic (theme) row: the theme label + one bar per variant. Kept as its own component so
// it can render both stacked (chat) and as a single carousel slide (report, compact).
function ThemeBarRow({
  theme,
  variantColor,
  comparisonLabels = [],
  hideSentimentChip = false,
}: {
  theme: MonadicThemeT
  variantColor: Record<string, string>
  comparisonLabels?: string[]
  // When the row lives under a sentiment-group header, the header already carries the chip —
  // skip the inline one to avoid a redundant duplicate.
  hideSentimentChip?: boolean
}) {
  const otherLabel = useThemeOtherLabel()
  const otherDescriptions = useThemeOtherDescriptions()

  return (
    <div data-testid="qs-monadic-theme-row">
      <div className="mb-1.5 flex items-center gap-2">
        <p
          className="text-sm font-medium text-twin-chat-fg"
          title={isOtherTheme(theme) ? otherDescriptions.mixed : theme.description || theme.theme}
        >
          <ComparisonLabelText text={themeDisplayName(theme, otherLabel)} labels={comparisonLabels} />
        </p>
        {!hideSentimentChip && !isOtherTheme(theme) && <SentimentChip sentiment={theme.sentiment} />}
      </div>
      <div className="space-y-1">
        {theme.variants.map((v) => (
          <div key={v.creative_id} className="flex items-center gap-2" data-testid="qs-monadic-variant-bar">
            <span className="w-24 shrink-0 truncate text-xs text-twin-chat-fg-muted" title={v.label}>
              <ComparisonLabelText text={v.label} labels={comparisonLabels} />
            </span>
            <div className={cn('flex-1 overflow-hidden rounded-full', THEME_BAR_HEIGHT_CLASS, THEME_BAR_TRACK_CLASS)}>
              {v.percent > 0 && (
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.max(3, Math.round(v.percent))}%`, background: variantColor[v.creative_id] }}
                />
              )}
            </div>
            <span className="w-16 shrink-0 text-right text-xs tabular-nums text-twin-chat-fg-muted">
              {v.percent}% · {v.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Exported so the combo Drivers tab (MessageRow's `comboOeRegion`) can reuse the SAME
// "shared theme label + one sub-bar per version" renderer for a monadic combo, instead of
// re-implementing it. Rendering/behavior are unchanged — this only exposes the component.
export function GroupedThemeBar({
  themes,
  perItem,
  variantColor,
  comparisonLabels = [],
  asCarousel = false,
  documentMode = false,
  themesView = 'heatmap',
  plot = null,
  heatPlot = null,
}: {
  themes: MonadicThemeT[]
  perItem: MonadicPerItem[]
  variantColor: Record<string, string>
  comparisonLabels?: string[]
  // Report (compact) only — page the topics through a carousel instead of stacking them.
  asCarousel?: boolean
  documentMode?: boolean
  // Chart-type choice for the themes view: heatmap (default, for 2+ columns) or grouped bars.
  themesView?: 'heatmap' | 'bars'
  // Wave 7 (Q13): the stamped grouped-bars plot; when present it replaces the browser bars.
  plot?: MonadicThemePlot | null
  // W7-T4b (Q14): the stamped heat grid; when present it replaces the browser heatmap.
  heatPlot?: ThemeHeatPlot | null
}) {
  const otherLabel = useThemeOtherLabel()

  if (themes.length === 0) {
    // No shared codeframe: each version's stamped themes draw as small multiples (wave 7 f1);
    // a stored result keeps the per-variant fallback bars, so the tab is never blank.
    const perVersionPlots = readPerVersionThemePlots(perItem)
    if (perVersionPlots.length > 0) {
      return <PerVersionThemesPlot versions={perVersionPlots} variantColor={variantColor} comparisonLabels={comparisonLabels} documentMode={documentMode} />
    }
    return (
      <PerVariantThemeFallback
        perItem={perItem}
        variantColor={variantColor}
        comparisonLabels={comparisonLabels}
        documentMode={documentMode}
      />
    )
  }
  const columns = deriveThemeColumns(perItem, themes)
  // Heatmap is the default themes view for 2+ assets and takes precedence over the carousel
  // (it is already compact). A single column has nothing to compare across, so it stays on bars.
  if (themesView === 'heatmap' && columns.length >= 2) {
    if (heatPlot) {
      return <ThemeHeatGridPlot plot={heatPlot} documentMode={documentMode} testIdPrefix="qs-monadic-theme-heat-grid" />
    }
    return (
      <div className="px-1 pt-1">
        <MonadicThemeHeatmap themes={themes} columns={columns} comparisonLabels={comparisonLabels} />
      </div>
    )
  }
  // A stamped codeframe draws through the shared grouped bars (compact already: no carousel).
  if (plot) {
    return <MonadicThemesPlot plot={plot} documentMode={documentMode} />
  }
  // Report card with more than one topic → carousel (one topic per slide, all variants per
  // slide) so a many-theme card stays compact instead of a tall stacked column.
  if (asCarousel && themes.length > 1) {
    return (
      <div className="px-1 pt-1" data-testid="qs-monadic-grouped-bar">
        <PlotCarousel
          ariaLabel="Themes by topic"
          slides={themes.map((t) => ({
            key: t.theme,
            label: themeDisplayName(t, otherLabel),
            node: (
              <ThemeBarRow theme={t} variantColor={variantColor} comparisonLabels={comparisonLabels} />
            ),
          }))}
        />
      </div>
    )
  }
  // Grouped stacked bars — sentiment-sectioned (Positive → Mixed → Negative), Other last.
  const { groups, other } = groupThemesBySentiment(themes)
  return (
    <div className="space-y-4 px-1 pt-1" data-testid="qs-monadic-grouped-bar">
      {groups.map((g) => (
        <div key={g.group} data-testid="qs-monadic-theme-group" data-group={g.group} className="space-y-3">
          <SentimentGroupChip group={g.group} />
          {g.themes.map((t) => (
            <ThemeBarRow
              key={t.theme}
              theme={t}
              variantColor={variantColor}
              comparisonLabels={comparisonLabels}
              hideSentimentChip
            />
          ))}
        </div>
      ))}
      {other.map((t) => (
        <ThemeBarRow
          key={t.theme}
          theme={t}
          variantColor={variantColor}
          comparisonLabels={comparisonLabels}
          hideSentimentChip
        />
      ))}
    </div>
  )
}

// ── quotes (chipped, merged) ────────────────────────────────────────────────────
function VariantChip({ label, color }: { label: string; color: string }) {
  return (
    <span
      className="flex shrink-0 items-center gap-1 rounded-full border border-twin-chat-divider px-2 py-0.5 text-[11px] font-medium text-twin-chat-fg-muted"
      data-testid="qs-monadic-quote-chip"
    >
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  )
}

function VariantSectionHeading({
  label,
  color,
}: {
  label: string
  color: string
  comparisonLabels?: string[]
}) {
  return (
    <h3
      className="flex items-center gap-2 text-base font-medium text-foreground"
      data-testid="qs-monadic-quote-variant-heading"
    >
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
      {label}
    </h3>
  )
}

type MonadicQuoteEntry = {
  qi: QuoteItem
  cid: string
  label: string
}

function buildMonadicQuoteEntries(
  perItem: MonadicPerItem[],
  question?: Record<string, unknown>,
  personas?: QuickStudyPersona[],
): MonadicQuoteEntry[] {
  return perItem.flatMap((item) =>
    buildQuoteItems(item.responses, question, personas ?? []).map((qi) => ({
      qi,
      cid: item.creative_id,
      label: item.label,
    })),
  )
}

const MONADIC_QUOTES_PREVIEW = 6

type MonadicQuoteGridGroup = {
  cid: string
  label: string
  entries: MonadicQuoteEntry[]
}

function MonadicQuoteVariantSection({
  group,
  groupByVariant,
  variantColor,
  comparisonLabels = [],
  documentMode = false,
}: {
  group: MonadicQuoteGridGroup
  groupByVariant: boolean
  variantColor: Record<string, string>
  comparisonLabels?: string[]
  documentMode?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const total = group.entries.length
  const hasMore = !documentMode && total > MONADIC_QUOTES_PREVIEW
  const visibleEntries =
    expanded || !hasMore ? group.entries : group.entries.slice(0, MONADIC_QUOTES_PREVIEW)

  return (
    <div
      className="space-y-2"
      data-testid="qs-monadic-quote-variant-section"
      data-variant-id={group.cid}
    >
      {groupByVariant && (
        <VariantSectionHeading
          label={group.label}
          color={variantColor[group.cid]}
          comparisonLabels={comparisonLabels}
        />
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {visibleEntries.map(({ qi, cid, label }) => (
          <QuoteCard
            key={`${cid}-${qi.key}`}
            item={qi}
            documentMode={documentMode}
            chip={
              groupByVariant ? undefined : (
                <VariantChip label={label} color={variantColor[cid]} />
              )
            }
            comparisonLabels={comparisonLabels}
          />
        ))}
      </div>
      {hasMore && (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="rounded-full border border-twin-chat-divider bg-twin-chat-surface px-4 py-1.5 text-sm font-medium text-twin-chat-fg transition-colors hover:bg-twin-chat-bg"
            data-testid="qs-monadic-quote-load-toggle"
            data-variant-id={group.cid}
          >
            {expanded ? 'Load less' : 'Load all'}
          </button>
        </div>
      )}
    </div>
  )
}

function MonadicQuotesView({
  perItem,
  question,
  personas,
  variantColor,
  comparisonLabels = [],
  documentMode = false,
}: {
  perItem: MonadicPerItem[]
  question?: Record<string, unknown>
  personas?: QuickStudyPersona[]
  variantColor: Record<string, string>
  comparisonLabels?: string[]
  documentMode?: boolean
}) {
  const groupByVariant = perItem.length > 1

  const variantGroups = useMemo(
    () =>
      perItem
        .map((item) => ({
          cid: item.creative_id,
          label: item.label,
          quotes: buildQuoteItems(item.responses, question, personas ?? []),
        }))
        .filter((group) => group.quotes.length > 0),
    [perItem, question, personas],
  )

  const allItems = useMemo(
    () => buildMonadicQuoteEntries(perItem, question, personas),
    [perItem, question, personas],
  )

  if (allItems.length === 0) {
    return (
      <p className="px-1 py-6 text-center text-sm text-twin-chat-fg-muted" data-testid="qs-monadic-quotes">
        No verbatims were captured for these variants.
      </p>
    )
  }

  const featuredIndex = pickFeaturedIndex(allItems.map(({ qi }) => qi))
  const featured = allItems[featuredIndex]

  const gridGroups = useMemo(() => {
    if (!groupByVariant) {
      const rest = allItems.filter((_, i) => i !== featuredIndex)
      return rest.length > 0 ? [{ cid: featured.cid, label: featured.label, entries: rest }] : []
    }
    return variantGroups
      .map((group) => ({
        cid: group.cid,
        label: group.label,
        entries: group.quotes
          .filter((qi) => !(qi.key === featured.qi.key && group.cid === featured.cid))
          .map((qi) => ({ qi, cid: group.cid, label: group.label })),
      }))
      .filter((group) => group.entries.length > 0)
  }, [allItems, featured, featuredIndex, groupByVariant, variantGroups])

  return (
    <div className="space-y-4" data-testid="qs-monadic-quotes">
      <FeaturedQuote
        item={featured.qi}
        chip={<VariantChip label={featured.label} color={variantColor[featured.cid]} />}
        comparisonLabels={comparisonLabels}
      />
      {gridGroups.length > 0 && (
        <div className="space-y-4" data-testid="qs-monadic-quote-grid">
          {gridGroups.map((group) => (
            <MonadicQuoteVariantSection
              key={group.cid}
              group={group}
              groupByVariant={groupByVariant}
              variantColor={variantColor}
              comparisonLabels={comparisonLabels}
              documentMode={documentMode}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ── card ──────────────────────────────────────────────────────────────────────
const TAB_LABELS: Record<MonadicTab, string> = {
  themes: 'Themes',
  quotes: 'Quotes',
}

/**
 * Available tabs for a monadic open-end card — mirrors the internal tab derivation EXACTLY so an
 * external (controlled) header toggle stays in lockstep with the card's own toggle. Pure.
 *   - documentMode → `[]` (both views render inline, no toggle).
 *   - otherwise → `['themes']`, plus `'quotes'` when quotes are shown (`showQuotes`).
 * `showQuotes` is the card's own `hasQuotes && (documentMode || !hideQuotes)` — callers pass it in.
 */
export function availableMonadicTabs({
  documentMode,
  showQuotes,
}: {
  documentMode: boolean
  showQuotes: boolean
}): Array<MonadicTab> {
  if (documentMode) {
    return []
  }
  return ['themes', ...(showQuotes ? (['quotes'] as Array<MonadicTab>) : [])]
}

export function MonadicOpenEndCard({
  monadicThemes,
  perItem,
  question,
  personas,
  comparisonLabels = [],
  construct,
  themesAsCarousel = false,
  hideQuotes = false,
  documentMode = false,
  activeTab,
  onTabChange,
  themesView = 'heatmap',
  monadicThemePlot,
  monadicThemeHeatPlot,
  quotePlot,
  className,
}: MonadicOpenEndCardProps) {
  const palette = useMemo(() => getAppChartPalette(), [])
  // A single uniform color for EVERY creative. Creatives are already identified by their text
  // label next to each bar / on each quote chip, so we do NOT color-code A vs B here. Distinct
  // per-creative colors belong only inside a dedicated same-plot comparison chart, never these
  // labeled theme-bar readouts, per-variant sections, or quote chips.
  const variantColor = useMemo(
    () => buildVariantColors(perItem, palette),
    [perItem, palette],
  )

  const hasQuotes = useMemo(
    () => perItem.some((v) => (v.responses?.length ?? 0) > 0),
    [perItem],
  )

  const quoteCount = useMemo(
    () => buildMonadicQuoteEntries(perItem, question, personas).length,
    [perItem, question, personas],
  )

  // Stamped reads (wave 7) draw through the shared plots; stored results keep the legacy views.
  const stampedThemes = readMonadicThemePlot(monadicThemePlot)
  const stampedHeat = readThemeHeatPlot(monadicThemeHeatPlot)
  const stampedQuotes = readQuotePlot(quotePlot)

  const showQuotes = hasQuotes && (documentMode || !hideQuotes)
  const tabs = availableMonadicTabs({ documentMode, showQuotes })
  const useCarousel = !documentMode && themesAsCarousel

  const [tab, setTab] = useState<MonadicTab>('themes')
  // Controlled (chat / PlotCard) when `activeTab` is provided — render only that view and let the
  // upstream PlotCard own the toggle + construct title. Otherwise uncontrolled: local tab state.
  const isControlled = activeTab !== undefined
  const resolvedTab: MonadicTab =
    activeTab !== undefined ? activeTab : tabs.includes(tab) ? tab : 'themes'

  const hasThemeContent =
    monadicThemes.length > 0 ||
    perItem.some((item) => (item.themes?.length ?? 0) > 0)

  if (documentMode && !hasThemeContent) {
    return null
  }

  return (
    <div className={cn('w-full space-y-3', className)} data-testid="qs-monadic-open-end-card">
      {/* Construct title (Result.title) — the same small bold label the pre-merge card wore
          via QsCardChrome's `construct` header slot, restored here directly (no full chrome
          wrapper) so Nicole's tab/layout markup below is untouched. */}
      {documentMode ? (
        <>
          {construct && (
            <p className="text-sm font-semibold text-twin-chat-fg" data-testid="qs-card-chrome-construct">
              {construct}
            </p>
          )}
          <GroupedThemeBar
            themes={monadicThemes}
            perItem={perItem}
            variantColor={variantColor}
            comparisonLabels={comparisonLabels}
            asCarousel={false}
            documentMode
            themesView={themesView}
            plot={stampedThemes}
            heatPlot={stampedHeat}
          />
        </>
      ) : (
        <>
      {/* Header row — the construct title on the LEFT and the Themes/Quotes toggle pulled to the
          far RIGHT on the SAME line (the quotes count sits just left of the toggle when the Quotes
          tab is open). Suppressed entirely in controlled mode: the PlotCard upstream owns the
          construct title AND the toggle (the quotes count relocates to the top of the quotes view). */}
      {!isControlled && (construct || tabs.length > 1 || (resolvedTab === 'quotes' && showQuotes)) && (
      <div className="flex items-center justify-between gap-3">
        {construct ? (
          <p className="min-w-0 text-sm font-semibold text-twin-chat-fg" data-testid="qs-card-chrome-construct">
            {construct}
          </p>
        ) : (
          <span aria-hidden />
        )}
        <div className="flex shrink-0 items-center gap-3">
          {resolvedTab === 'quotes' && showQuotes && !stampedQuotes && (
            <MonadicQuotesCount count={quoteCount} />
          )}
          {tabs.length > 1 && (
            <div className="flex shrink-0 items-center gap-0.5 rounded-full border border-twin-chat-divider bg-twin-chat-bg p-0.5">
              {tabs.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTab(t)
                    onTabChange?.(t)
                  }}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                    resolvedTab === t
                      ? 'bg-twin-chat-surface text-twin-chat-fg'
                      : 'text-twin-chat-fg-muted hover:text-twin-chat-fg',
                  )}
                  data-testid={`qs-monadic-tab-${t}`}
                >
                  {TAB_LABELS[t]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {resolvedTab === 'themes' && (
        <GroupedThemeBar
          themes={monadicThemes}
          perItem={perItem}
          variantColor={variantColor}
          comparisonLabels={comparisonLabels}
          asCarousel={useCarousel}
          themesView={themesView}
          plot={stampedThemes}
          heatPlot={stampedHeat}
        />
      )}
      {resolvedTab === 'quotes' && (
        <>
          {/* In controlled mode the header row (with its quotes count) is suppressed, so the count
              rides down here — it is view content (how many verbatims), not chrome, and must not
              silently disappear (plan review F6). */}
          {isControlled && showQuotes && !stampedQuotes && <MonadicQuotesCount count={quoteCount} />}
          {stampedQuotes ? (
            // The gallery prints its own count (Python's quotes, as stamped).
            <OpenEndQuotesPlot plot={stampedQuotes} personas={personas} comparisonLabels={comparisonLabels} />
          ) : (
            <MonadicQuotesView
              perItem={perItem}
              question={question}
              personas={personas}
              variantColor={variantColor}
              comparisonLabels={comparisonLabels}
            />
          )}
        </>
      )}
        </>
      )}
    </div>
  )
}

export default MonadicOpenEndCard
